<template>
  <div class="FormDate" @keyup.capture="updateValue">
    <input
      v-if="showMonth"
      ref="month"
      v-model="month"
      class="FormDate__input FormDate__input--month"
      type="number"
      placeholder="MM"
      :aria-label="monthAriaLabel"
      @input="updateMonth"
      @keypress="isDateDigit('month', $event)"
      @blur="month = month.padStart(2, 0)"
      maxlength="2"
    />
    <span v-if="showDay && showMonth" class="FormDate__divider">/</span>
    <input
      v-if="showDay"
      ref="day"
      v-model="day"
      class="FormDate__input FormDate__input--day"
      type="number"
      placeholder="DD"
      :aria-label="dayAriaLabel"
      @input="updateDay"
      @keypress="isDateDigit('day', $event)"
      @blur="day = day.padStart(2, 0)"
      maxlength="2"
    />
    <span v-if="showYear && (showDay || showMonth)" class="FormDate__divider"
      >/</span
    >
    <input
      v-if="showYear"
      ref="year"
      v-model="year"
      class="FormDate__input FormDate__input--year"
      type="number"
      placeholder="YYYY"
      :aria-label="yearAriaLabel"
      @keypress="isDateDigit('year', $event)"
      @blur="year = year.padStart(4, 0)"
      maxlength="4"
    />
  </div>
</template>

<script>
export default {
  name: `FormDate`,
  props: {
    value: {
      type: [Number, String],
      required: true,
    },
    showDay: {
      type: Boolean,
      default: true,
    },
    showMonth: {
      type: Boolean,
      default: true,
    },
    showYear: {
      type: Boolean,
      default: true,
    },
    dayAriaLabel: {
      type: String,
      default: 'Enter day',
    },
    monthAriaLabel: {
      type: String,
      default: 'Enter month',
    },
    yearAriaLabel: {
      type: String,
      default: 'Enter year',
    },
  },
  data() {
    return {
      day: `${this.value ? new Date(this.value).getDate() : ``}`,
      month: `${this.value ? new Date(this.value).getMonth() + 1 : ``}`,
      year: `${this.value ? new Date(this.value).getFullYear() : ``}`,
    };
  },
  watch: {
    month(current) {
      if (current > 12 || current === '00') this.month = '';
      if (current !== '') {
        this.updateMonth();
        this.updateValue();
      }
    },
    day(current) {
      if (current > 32 || current === '00') this.day = '';
      if (current !== '') {
        this.updateDay();
        this.updateValue();
      }
    },
    year(current) {
      if (current === '0000') this.year = '';
    },
  },
  methods: {
    isNumber(e) {
      let char = String.fromCharCode(e.keyCode);
      return /^[0-9]*$/.test(char) ? true : false;
    },
    updateMonth() {
      if (!this.month.length || parseInt(this.month, 10) < 2) return;
      if (this.year && this.day) this.updateValue();
      if (this.showDay) this.$refs.day.select();
      else if (this.showYear) this.$refs.year.select();
    },
    updateDay() {
      if (!this.day.length || parseInt(this.day, 10) < 4) return;
      if (this.year && this.month) this.updateValue();
      if (this.showYear) this.$refs.year.select();
    },
    updateValue() {
      if (!this.month || !this.day) return;

      const finalDate = `${this.month}/${this.day}/${this.year.padStart(4, 0)}`;

      const actualYear = new Date().getFullYear();
      const regexDate = /^\d{2}\/\d{2}\/\d{4}$/;
      const year = parseInt(this.year, 10);

      if (regexDate.test(finalDate) && year > 0 && year >= actualYear) {
        this.$emit('input', finalDate);
        this.$emit('isRealDate', true);
      } else {
        this.$emit('isRealDate', false);
      }
    },
    isDateDigit(field, e) {
      if (!this.isNumber) {
        e.preventDefault();
      } else {
        if (field === 'day' && this.day.length > 1) this.day = '';
        if (field === 'month' && this.month.length > 1) this.month = '';
        if (field === 'year' && this.year.length > 3) this.year = '';
      }
    },
  },
};
</script>

<style lang="stylus">
@import '~@discoveryeducation/theme/src/mixins.styl'

.FormDate {
  letter-spacing 0.75em
  display inline-flex
  position relative
  overflow hidden
  outline(transparent)

  &__input {
    border none
    text-align center
    background-color transparent
    -moz-appearance textfield
    &::-webkit-inner-spin-button {
      display none
    }
    &:focus {
      outline none
      border none
    }
    &--day,
    &--month {
      width 2em
    }
    &--year {
      width 3em
    }
  }
  &__divider {
    pointer-events none
    width 0.4em
  }
}
</style>
